import {
  applySnapshot,
  cast,
  flow,
  getSnapshot,
  Instance,
  types,
} from 'mobx-state-tree';
import find from 'lodash/find';
import moment from 'moment';
import { RequestStateModel } from '../general-models';
import { Reports, Meters } from 'http-requests';
import { IParams } from 'http-requests/types';
import { AreaMeterModel } from '../metersStore';
import { killNulls } from 'utils';
import { IObject } from 'types';
import { ISelectValue } from 'components/ui';
import {
  mergeHistoryRequest,
  TMeterRequestPayload,
} from './history-meters-request';
import {
  ICoordinate,
  IGroup,
  IUpdateParam,
} from 'views/authorized/pages/reports/tabs/overdue-meters/components/map/types';

const RequestsStatusesModel = types.model({
  createReport: types.optional(RequestStateModel, {}),
  getPoint: types.optional(RequestStateModel, {}),
  createExcelReport: types.optional(RequestStateModel, {}),
  getAreaOverdueMeters: types.optional(RequestStateModel, {}),
  createGroupsReport: types.optional(RequestStateModel, {}),
  updateGroupsReport: types.optional(RequestStateModel, {}),
});

const ExtraDataRequestsStatusesModel = types.model({
  createHouseReport: types.optional(RequestStateModel, {}),
});

const GroupsColor = types.model({
  color: types.optional(types.string, ''),
  border_color: types.optional(types.string, ''),
  fill_color: types.optional(types.string, ''),
});

const GroupsPinNameAndCenter = types.model({
  type: types.optional(types.string, ''),
  coordinates: types.optional(types.array(types.number), []),
});

const GroupsPolygon = types.model({
  type: types.optional(types.string, ''),
  coordinates: types.optional(
    types.array(types.array(types.array(types.number))),
    []
  ),
});

//Модель создания новой группы
const GroupModel = types.model({
  name: types.optional(types.string, ''),
  colors: types.optional(GroupsColor, {}),
  polygon: types.optional(types.array(types.array(types.number)), [[0, 0]]),
  pin_name: types.optional(types.array(types.number), [0, 0]),
  center: types.optional(types.array(types.number), [0, 0]),
});

export interface IGroupModel extends Instance<typeof GroupModel> {}

const FiltersModel = types
  .model({
    date: types.optional(
      types.maybeNull(types.string),
      moment().format('YYYY-MM-DDTHH:mm:ss')
    ),
    meter_types: types.optional(types.array(types.string), []),
    statuses: types.optional(types.array(types.string), []),
    contacts: types.optional(types.maybeNull(types.string), null),
    stage: types.optional(types.array(types.string), []),
    start_date: types.optional(types.maybeNull(types.string), null),
    end_date: types.optional(types.maybeNull(types.string), null),
    request_statuses: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    setDate(date: string | null) {
      self.date = date;
    },
    setStartDate(date: string | null) {
      self.start_date = date;
    },
    setEndDate(date: string | null) {
      self.end_date = date;
    },
  }));

export interface IFiltersModel extends Instance<typeof FiltersModel> {}

const ParamsModel = types
  .model({
    overdue_meters_groups: types.optional(types.array(types.string), []),
    filters: types.optional(FiltersModel, {}),
    newGroupData: types.optional(GroupModel, {}),
    fias: types.optional(types.array(types.string), []),
    house_groups: types.optional(types.array(types.string), []),
    houses: types.optional(types.array(types.string), []),
    str_number: types.optional(types.string, ''), //номер телефона
    // on_page: types.optional(types.number, 20), //лимит запроса
    // page: types.optional(types.number, 1), //номер страницы
  })
  .views((self) => ({
    get isDisabled() {
      return !(
        self.filters.date &&
        (self.fias.length ||
          self.house_groups.length ||
          self.houses.length ||
          self.overdue_meters_groups.length ||
          (self.str_number.length && self.str_number.length === 10))
      );
    },
    get isHavingFiasDisabledOther() {
      return !!(
        self.fias.length ||
        self.house_groups.length ||
        self.houses.length
      );
    },
    get mapGroupsParams() {
      return {
        filters: self.filters,
        overdue_meters_groups: self.overdue_meters_groups,
      };
    },
    get phoneParams() {
      return {
        filters: self.filters,
        str_number: self.str_number,
        // on_page: self.on_page,
        // page: self.page,
      };
    },
    get addressParams() {
      return {
        filters: self.filters,
        overdue_meters_groups: self.overdue_meters_groups,
        fias: self.fias,
        house_groups: self.house_groups,
        houses: self.houses,
        // on_page: self.on_page,
        // page: self.page,
      };
    },
    get pointParams() {
      return {
        filters: self.filters,
        overdue_meters_groups: self.overdue_meters_groups,
        fias: self.fias,
        house_groups: self.house_groups,
        houses: self.houses,
        str_number: self.str_number.length ? self.str_number : null,
      };
    },
    get newGroupParams() {
      return {
        name: self.newGroupData.name,
        colors: {
          color: self.newGroupData.colors.color,
          border_color: self.newGroupData.colors.border_color,
          fill_color: self.newGroupData.colors.fill_color,
        },
        polygon: {
          coordinates: self.newGroupData.polygon,
        },
        pin_name: {
          coordinates: self.newGroupData.pin_name,
        },
        center: {
          coordinates: self.newGroupData.center,
        },
        filters: self.filters,
        overdue_meters_groups: [],
        fias: self.fias,
        house_groups: self.house_groups,
        houses: self.houses,
        str_number: self.str_number.length ? self.str_number : null,
      };
    },
    get filtersCount(): number {
      let count = 0;
      let filters: { [index: string]: any } = self.filters;

      if (
        !!self.fias.length ||
        !!self.house_groups.length ||
        !!self.houses.length ||
        !!self.overdue_meters_groups.length ||
        (self.str_number.length && self.str_number.length === 11)
      ) {
        count += 1;
      }

      //TODO в будущем опитмизировать
      for (let key in self.filters) {
        if (filters[key] !== null || filters[key] !== undefined) {
          if (Array.isArray(filters[key])) {
            if (filters[key].length) {
              count += 1;
            }
          } else if (typeof filters[key] === 'object') {
            if (Object.keys(filters[key] || {}).length) {
              count += 1;
            }
          } else if (typeof filters[key] === 'string') {
            if (filters[key].length) {
              count += 1;
            }
          } else {
            count += 1;
          }
        }
      }

      return count;
    },

    get excelParams() {
      return {
        filters: self.filters,
        fias: self.fias,
        house_groups: self.house_groups,
        houses: self.houses,
        overdue_meters_groups: self.overdue_meters_groups,
      };
    },
  }))
  .actions((self) => ({
    setFias(aoguids: string[]) {
      self.fias = cast(aoguids);
    },
    setHouseGroups(houseGroups: string[]) {
      self.house_groups = cast(houseGroups);
    },
    setHouses(houses: string[]) {
      self.houses = cast(houses);
    },
    setStrNumber(phone: string) {
      self.str_number = phone;
    },
    setOverdueGroup(ids: string[]) {
      self.overdue_meters_groups = cast(ids);
    },
    setNewGroupData(groups: IGroupModel) {
      self.newGroupData = cast(groups);
    },
    setNewGroupPolygonCoordinates(lngLat: number[][]) {
      self.newGroupData.polygon = cast(lngLat);
    },
    setNewGroupCenterCoordinates(lngLat: number[]) {
      self.newGroupData.center = cast(lngLat);
    },
    setNewGroupPinNameCoordinates(lngLat: number[]) {
      self.newGroupData.pin_name = cast(lngLat);
    },
    setFilters(param: IFiltersModel) {
      self.filters = param;
    },
    clearOverdueGroup() {
      applySnapshot(self.overdue_meters_groups, []);
    },
    clearFiasParam() {
      applySnapshot(self.houses, []);
      applySnapshot(self.house_groups, []);
      applySnapshot(self.fias, []);
    },
    clearFilterParams() {
      applySnapshot(self.filters, {});
    },
  }));

const CountModel = types.model({
  meter_type: types.optional(types.string, ''),
  number: types.optional(types.number, 0),
});

const MonthModel = types.model({
  month: types.optional(types.string, ''),
  count_by_type: types.optional(types.array(CountModel), []),
});

export interface IMonthModel extends Instance<typeof MonthModel> {}

const PhoneModel = types.model({
  code: types.optional(types.maybeNull(types.string), ''),
  number: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  str_number: types.optional(types.maybeNull(types.string), null),
});

export interface IPhoneModel extends Instance<typeof PhoneModel> {}

const HistoryMetersStatusModel = types.model({
  status_data: types.optional(types.string, ''),
  communication: types.optional(types.maybeNull(types.string), null),
  extra: types.optional(types.maybeNull(types.string), null),
  date: types.optional(types.maybeNull(types.string), null),
});

export interface IHistoryMetersStatusModel extends Instance<
  typeof HistoryMetersStatusModel
> {}

const CommentModel = types.model({
  comment_data: types.optional(types.maybeNull(types.string), null),
  date: types.optional(types.maybeNull(types.string), null),
});

const MeterRequestModel = types.model({
  id: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  created_at: types.optional(types.string, ''),
  common_status: types.optional(types.string, ''),
});

const HistoryMetersModel = types
  .model({
    area: types.optional(types.string, ''),
    id: types.optional(types.string, ''),
    comment: types.optional(types.maybeNull(CommentModel), null),
    request: types.optional(types.maybeNull(MeterRequestModel), null),
    status: types.optional(types.array(HistoryMetersStatusModel), []),
    meters_ids: types.optional(types.array(types.string), []),
    isRejected: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get historySorted() {
      //упорядоченная история, с подмешанным комментом и номером заявки (при наличии)
      let result = [...self.status];
      let request = null;
      let comment = null;

      if (self.comment?.comment_data) {
        comment = HistoryMetersStatusModel.create({
          status_data: self.comment.comment_data,
          communication: 'comment',
          extra: null,
          date: self.comment.date,
        });
        result.push(comment);
      }

      if (self.request) {
        request = HistoryMetersStatusModel.create({
          status_data: 'Создана заявка',
          communication: 'request',
          extra: `№${self.request.number}`,
          date: self.request.created_at,
        });
        result.push(request);
      }

      if (comment || request) {
        result.sort((a, b) => moment(a.date).diff(moment(b.date)));
      }

      return result.reverse();
    },
  }))
  .actions((self) => ({
    updateMeters: flow(function* (meters: string[]) {
      try {
        const response = yield Reports.UpdateHistoryMetersList({
          area: self.area,
          history_id: self.id,
          meters: meters,
        });
        self.meters_ids = response.meters_ids;
        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    updateStatus: flow(function* (newStatus: IParams) {
      try {
        const response = yield Reports.UpdateHistoryMetersStatus({
          area: self.area,
          history_id: self.id,
          status: newStatus,
        });
        self.status = response.status;
        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    updateComment: flow(function* (comment: string) {
      try {
        const response = yield Reports.UpdateHistoryMetersComment({
          area: self.area,
          history_id: self.id,
          comment: {
            comment_data: comment,
          },
        });
        self.comment = response.comment;
        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    updateRequest: flow(function* (request: TMeterRequestPayload) {
      try {
        const response = yield Reports.UpdateHistoryMetersRequest({
          area: self.area,
          history_id: self.id,
          request,
        });
        self.request = mergeHistoryRequest(request, response.request);

        return response;
      } catch (e) {
        console.error(e);
      }
    }),
    deleteHistory: flow(function* () {
      try {
        const response = yield Reports.DeleteHistoryMeters(self.id);
        self.comment = null;
        self.request = null;
        applySnapshot(self.meters_ids, []);
        applySnapshot(self.status, []);
        self.isRejected = true;
        return response;
      } catch (e) {
        console.error(e);
      }
    }),
  }));

const ExtraDataModel = types.model({
  area: types.optional(types.identifier, ''), //id помещения
  area_number: types.optional(types.string, ''), //номер помещения
  porch_number: types.optional(types.number, 0), //номер парадной
  str_name: types.optional(types.string, ''), //имя собственника
  email: types.optional(types.string, ''), //контакты собственника
  count: types.optional(types.number, 0), //количество просроченных и просрачиваемых счётчиков
  next_check_date: types.optional(types.string, ''), //дата поверки самого раннего счётчика
  last_readings_date: types.optional(types.maybeNull(types.string), null), //последняя дата подачи показаний самого раннего счётчика
  tenant: types.optional(types.string, ''), //id жителя
  house: types.optional(types.string, ''), //id дома в котором эта квартира
  phones: types.optional(types.array(PhoneModel), []), //номера телефонов жителя
  debt: types.optional(types.number, 0), //долг по КУ
  history_meters: types.optional(HistoryMetersModel, {}), //история работы с созданием заявки на замену счётчиков
});

export interface IExtraDataModel extends Instance<typeof ExtraDataModel> {}

const DataModel = types
  .model({
    id: types.optional(types.string, ''), //id дома //раньше называлось house
    address: types.optional(types.string, ''), //адрес дома
    past: types.optional(types.number, 0), //кол-во счётчиков с вышедшим сроком поверки
    months: types.optional(types.array(MonthModel), []),
    pages: types.optional(types.number, 0), //общее кол-во страниц вложенности
    currentPage: types.optional(types.number, 0), //текущая подгруженная страница вложенности
    extraData: types.optional(types.array(ExtraDataModel), []),
    extraDataStatuses: types.optional(ExtraDataRequestsStatusesModel, {}),
  })
  .actions((self) => ({
    resetExtraData() {
      applySnapshot(self.extraData, []);
    },
    createHouseReport: flow(function* (filters?: IObject) {
      try {
        self.extraDataStatuses.createHouseReport.fulfilled = false;
        self.extraDataStatuses.createHouseReport.error = false;
        self.extraDataStatuses.createHouseReport.pending = true;

        const params: { [key: string]: any } = {
          filters: {
            ...filters?.filters,
          },
          str_number: filters?.str_number,
          overdue_meters_groups: filters?.overdue_meters_groups,
          houses: [self.id],
          on_page: 25,
          page: self.currentPage + 1,
        };

        const response: IObject =
          yield Reports.CreateOverdueMetersReportByHouse(killNulls(params));

        self.extraData = cast([...self.extraData].concat(response.result)); //TODO: проверить
        self.pages = response.pages;
        self.currentPage = response.page;

        self.extraDataStatuses.createHouseReport.fulfilled = true;
        return response;
      } catch (e) {
        self.extraDataStatuses.createHouseReport.error = true;
        console.error(e);
      } finally {
        self.extraDataStatuses.createHouseReport.pending = false;
      }
    }),
  }));

export interface IDataModel extends Instance<typeof DataModel> {}

const MapPointModel = types.model({
  type: types.optional(types.string, ''),
  coordinates: types.optional(types.maybeNull(types.array(types.number)), []),
});

const MapDataModel = types.model('MapModel', {
  house_id: types.optional(types.string, ''),
  point: types.optional(MapPointModel, {}),
  total_meters: types.optional(types.number, 0),
});

export interface IMapDataModel extends Instance<typeof MapDataModel> {}

const GroupsModel = types.model({
  _id: types.optional(types.string, ''),
  provider_id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  colors: types.optional(GroupsColor, {}),
  polygon: types.optional(GroupsPolygon, {}),
  pin_name: types.optional(GroupsPinNameAndCenter, {}),
  center: types.optional(GroupsPinNameAndCenter, {}),
  created_at: types.optional(types.string, ''),
  filters: types.optional(FiltersModel, {}),
});

export interface IGroupsModel extends Instance<typeof GroupsModel> {}

const SelectedMapGroupsModel = types.model({
  label: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
});

const RequestProviderModel = types.model({
  _id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

export interface IRequestProviderModel extends Instance<
  typeof RequestProviderModel
> {}

const CreateMeterRequestModel = types
  .model({
    meters: types.optional(types.array(types.string), []),
    requestDescription: types.optional(types.string, ''),
    requestProvider: types.optional(RequestProviderModel, {}),
  })
  .actions((self) => ({
    resetData() {
      applySnapshot(self.meters, []);
      applySnapshot(self.requestProvider, {});
      self.requestDescription = '';
    },
    setRequestBody(value: string) {
      self.requestDescription = value;
    },
    setRequestProvider(value: IRequestProviderModel) {
      self.requestProvider = value;
    },
    addMeter(id: string) {
      self.meters = cast([...self.meters, id]);
    },
    removeMeter(id: string) {
      self.meters = cast(self.meters.filter((item) => item !== id));
    },
    reRecordMeters(ids: string[]) {
      self.meters = cast(ids);
    },
  }));

const GroupInfoModel = types
  .model({
    groupName: types.optional(types.string, ''),
    groupWindowMode: types.optional(types.string, ''),
    currentLineColor: types.optional(types.string, '#93C4FC'),
    currentBorderColor: types.optional(types.string, '#1D4ED6'),
    currentFillColor: types.optional(types.string, 'rgba(238, 245, 254, 0.5)'),
    error: types.optional(types.string, ''),
    polygonCoordinates: types.optional(
      types.array(types.array(types.maybeNull(types.number))),
      [[0, 0, null]]
    ),
  })
  .views((self) => ({
    get getPolygonCoordinates(): ICoordinate[] {
      return self.polygonCoordinates as unknown as ICoordinate[];
    },
  }))
  .actions((self) => ({
    setGroupName(name: string) {
      self.groupName = name;
    },
    setColor(fill: string, border: string, line: string) {
      self.currentLineColor = line;
      self.currentFillColor = fill;
      self.currentBorderColor = border;
    },
    setGroupWindowMode(mode: string) {
      self.groupWindowMode = mode;
    },
    setError(error: string) {
      self.error = error;
    },
    setPolygonCoordinates(coordinate: ICoordinate[]) {
      self.polygonCoordinates = cast(coordinate);
    },
    clearPolygonCoordinates() {
      applySnapshot(self.polygonCoordinates, [[0, 0, null]]);
    },
    clearColor() {
      self.currentFillColor = 'rgba(238, 245, 254, 0.5)';
      self.currentBorderColor = '#1D4ED6';
      self.currentLineColor = '#93C4FC';
    },
  }));

export const OverdueMetersReportStore = types
  .model({
    pages: types.optional(types.number, 0), //общее кол-во страниц
    currentPage: types.optional(types.number, 0), //текущая страница
    data: types.optional(types.array(DataModel), []),
    mapData: types.optional(types.array(MapDataModel), []),
    groupsData: types.optional(types.array(GroupsModel), []),
    currentGroupData: types.optional(GroupsModel, {}),
    params: types.optional(ParamsModel, {}),
    groupInfo: types.optional(GroupInfoModel, {}),
    selectedMapGroups: types.optional(types.array(SelectedMapGroupsModel), []),
    reportExcelOnProgressId: types.optional(
      types.maybeNull(types.string),
      null
    ),
    requestsStatuses: types.optional(RequestsStatusesModel, {}),
    selectedPreviousArea: types.maybeNull(
      types.late(() => types.reference(ExtraDataModel))
    ),
    selectedArea: types.maybeNull(types.reference(ExtraDataModel)), //selectedArea: types.optional(ExtraDataModel, {}),
    areaMeters: types.optional(types.array(AreaMeterModel), []),
    createMeterRequestData: types.optional(CreateMeterRequestModel, {}),
  })
  .views((self) => ({
    getPaginationParams(paramsType: 'phone' | 'address', page: number = 1) {
      const initialParams =
        paramsType === 'phone'
          ? self.params.phoneParams
          : self.params.addressParams;

      return {
        ...initialParams,
        on_page: 25,
        page: page,
      };
    },
    getGroupsData(): IGroup[] {
      return getSnapshot<IGroup[]>(self.groupsData);
    },
    get canUnloadExel(): boolean {
      return (
        !self.params.isDisabled &&
        !self.params.str_number.length &&
        !self.requestsStatuses.createExcelReport.pending
      );
    },
    get selectedMetersInfo() {
      return self.areaMeters.filter((meter) =>
        self.createMeterRequestData.meters.includes(meter.id)
      );
    },
    get onLoadData(): boolean {
      //TODO: раньше тут было более сложное условие, временно оставил этот вью, на случай отката изменений. Если так останется, то это нужно удалить
      return self.requestsStatuses.createReport.pending;
    },
    getHouseAddress(id: string) {
      const house = find(self.data, { id });
      return house?.address || '';
    },
  }))
  .actions((self) => ({
    clearGroupInfo() {
      applySnapshot(self.groupInfo, {});
    },
    clearData() {
      self.reportExcelOnProgressId = null;
      applySnapshot(self.data, []);
      applySnapshot(self.requestsStatuses, {});
    },
    setCurrentGroups(group: IGroup) {
      self.currentGroupData = cast(group);
    },
    clearCurrentGroup() {
      applySnapshot(self.currentGroupData, {});
    },
    setSelectedGroups(value: ISelectValue[]) {
      self.selectedMapGroups = cast(value);
    },
    clearSelectedGroups() {
      applySnapshot(self.selectedMapGroups, []);
    },
    clearCurtainData() {
      self.selectedArea = null;
      // applySnapshot(self.selectedArea, {});
      applySnapshot(self.areaMeters, []);
      applySnapshot(self.createMeterRequestData, {});
    },
    clearAreaMeters() {
      applySnapshot(self.areaMeters, []);
      //нужно чтобы если уже запрашиваются нужные счётчики в шторку, но пришли неактуальные с прошлой шторки и лоадер не пропадал
      self.requestsStatuses.getAreaOverdueMeters.pending = true;
    },
    selectArea(area: IExtraDataModel) {
      self.selectedArea = area;
    },
    selectPreviousArea(area: IExtraDataModel) {
      self.selectedPreviousArea = area;
    },
    clearExcelReport() {
      self.reportExcelOnProgressId = null;
    },
    addGroupsData(groups: IGroupsModel[]) {
      self.groupsData = cast(groups);
    },
    createExcelReport: flow(function* () {
      try {
        self.requestsStatuses.createExcelReport.pending = true;
        self.requestsStatuses.createExcelReport.error = false;
        self.requestsStatuses.createExcelReport.fulfilled = false;

        const response = yield Reports.CreateNewOverdueMetersReport(
          self.params.excelParams
        );
        self.reportExcelOnProgressId = response.report_id;

        self.requestsStatuses.createExcelReport.fulfilled = true;
      } catch (e) {
        self.requestsStatuses.createExcelReport.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.createExcelReport.pending = false;
      }
    }),
    getGroup: flow(function* () {
      try {
        self.requestsStatuses.updateGroupsReport.pending = true;
        self.requestsStatuses.updateGroupsReport.error = false;
        self.requestsStatuses.updateGroupsReport.fulfilled = false;

        const response = yield Reports.GetOverdueMetersReportGroup();
        self.groupsData = response.results;

        self.requestsStatuses.updateGroupsReport.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.updateGroupsReport.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.updateGroupsReport.pending = false;
      }
    }),
    deleteGroup: flow(function* (id: string) {
      try {
        self.requestsStatuses.updateGroupsReport.pending = true;
        self.requestsStatuses.updateGroupsReport.error = false;
        self.requestsStatuses.updateGroupsReport.fulfilled = false;

        const response = yield Reports.DeleteOverdueMetersReportGroup(id);

        self.groupsData = cast(
          self.groupsData.filter((item) => item._id !== response._id)
        );

        self.requestsStatuses.updateGroupsReport.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.updateGroupsReport.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.updateGroupsReport.pending = false;
      }
    }),
    updateGroup: flow(function* (id: string, params: IUpdateParam) {
      try {
        self.requestsStatuses.updateGroupsReport.pending = true;
        self.requestsStatuses.updateGroupsReport.error = false;
        self.requestsStatuses.updateGroupsReport.fulfilled = false;

        const response = yield Reports.UpdateOverdueMetersReportGroup(
          id,
          params
        );

        self.groupsData.find((group) => {
          if (group._id === response._id) {
            group.name = response.name;
            group.colors = response.colors;
          }
        });
        self.requestsStatuses.updateGroupsReport.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.updateGroupsReport.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.updateGroupsReport.pending = false;
      }
    }),
    createGroupsReport: flow(function* () {
      try {
        self.requestsStatuses.createGroupsReport.pending = true;
        self.requestsStatuses.createGroupsReport.error = false;
        self.requestsStatuses.createGroupsReport.fulfilled = false;

        const response = yield Reports.CreateOverdueMetersReportGroup(
          self.params.newGroupParams
        );
        self.groupsData.push(response);
        self.requestsStatuses.createGroupsReport.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.createGroupsReport.error = true;
        console.error(e);
        throw e;
      } finally {
        self.requestsStatuses.createGroupsReport.pending = false;
      }
    }),
    createPoint: flow(function* () {
      try {
        self.requestsStatuses.getPoint.pending = true;
        self.requestsStatuses.getPoint.error = false;
        self.requestsStatuses.getPoint.fulfilled = false;

        applySnapshot(self.mapData, []);

        const response = yield Reports.CreateOverdueMetersReportPoint(
          self.params.pointParams
        );
        self.mapData = response.result;

        self.requestsStatuses.getPoint.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.getPoint.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getPoint.pending = false;
      }
    }),
    createReport: flow(function* (page?: number) {
      try {
        self.requestsStatuses.createReport.pending = true;
        self.requestsStatuses.createReport.error = false;
        self.requestsStatuses.createReport.fulfilled = false;

        applySnapshot(self.data, []);
        applySnapshot(self.params.overdue_meters_groups, []);

        const response = yield Reports.CreateOverdueMetersReport(
          self.getPaginationParams(
            !!self.params.str_number.length ? 'phone' : 'address',
            page
          )
        );

        self.data = response.result;
        self.pages = response.pages;
        self.currentPage = response.page;

        self.requestsStatuses.createReport.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.createReport.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.createReport.pending = false;
      }
    }),
    createReportGroups: flow(function* () {
      try {
        self.requestsStatuses.createReport.pending = true;
        self.requestsStatuses.createReport.error = false;
        self.requestsStatuses.createReport.fulfilled = false;

        applySnapshot(self.data, []);
        applySnapshot(self.mapData, []);

        const response = yield Reports.CreateOverdueMetersReport(
          self.params.mapGroupsParams
        );

        self.data = response.result;
        self.pages = response.pages;
        self.currentPage = response.page;

        self.requestsStatuses.createReport.fulfilled = true;
        return response;
      } catch (e) {
        self.requestsStatuses.createReport.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.createReport.pending = false;
      }
    }),
    getAreaOverdueMeters: flow(function* (areaId: string) {
      try {
        self.requestsStatuses.getAreaOverdueMeters.pending = true;
        self.requestsStatuses.getAreaOverdueMeters.fulfilled = false;
        self.requestsStatuses.getAreaOverdueMeters.error = false;
        const response = yield Meters.GetAreaOverdueMeters(areaId, {
          date: self.params.filters.date,
        });
        self.areaMeters = response.results;
        self.requestsStatuses.getAreaOverdueMeters.fulfilled = true;
      } catch (e) {
        self.requestsStatuses.getAreaOverdueMeters.error = true;
        console.error(e);
      } finally {
        self.requestsStatuses.getAreaOverdueMeters.pending = false;
      }
    }),
  }));
